import {omneticApi} from '@dms/api/base';

import {
  FinancingEndingBulkOperationWireSchema,
  FinancingEndingEmailSourceResponseBodySchema,
  FinancingEndingPreflightResponseBodySchema,
  FinancingEndingPreviewResponseBodySchema,
  FinancingEndingSubmitBulkActionResponseBodySchema,
  ListFinancingEndingBulkOperationItemsWireSchema,
  RerunFailedFinancingEndingBulkOperationApiResponseSchema,
  type FinancingEndingBulkOperationWire,
  type GetFinancingEndingBulkOperationApiArg,
  type GetFinancingEndingBulkOperationApiResponse,
  type ListFinancingEndingBulkOperationItemsApiArg,
  type ListFinancingEndingBulkOperationItemsApiResponse,
  type ListFinancingEndingBulkOperationItemsWire,
  type PreflightFinancingEndingEmailApiArg,
  type PreflightFinancingEndingEmailApiResponse,
  type PrepareFinancingEndingEmailSourceApiArg,
  type PrepareFinancingEndingEmailSourceApiResponse,
  type PreviewEditableFinancingEndingEmailApiArg,
  type PreviewEditableFinancingEndingEmailApiResponse,
  type RerunFailedFinancingEndingBulkOperationApiArg,
  type RerunFailedFinancingEndingBulkOperationApiResponse,
  type SubmitFinancingEndingBulkActionApiArg,
  type SubmitFinancingEndingBulkActionApiResponse,
} from './types';
import {
  mapFinancingEndingBulkOperation,
  mapFinancingEndingBulkOperationItem,
} from './utils/mapFinancingEndingBulkOperation';

const FINANCING_ENDING_BULK_RESOURCE_ID = 'BUSINESS_CASE';
// RTK Query stops a poll in a batched 0 ms timer after the last unsubscribe. A removal timer that
// runs first leaves the poll running, and it sends one more request after the detail is closed.
const ITEMS_KEEP_UNUSED_DATA_FOR_SECONDS = 1;

const financingEndingBaseApi = omneticApi.enhanceEndpoints({
  addTagTypes: ['FinancingEndingBulkOperation', 'FinancingEndingBulkOperationItems'],
});

export const financingEndingApi = financingEndingBaseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    preflightFinancingEndingEmail: build.query<
      PreflightFinancingEndingEmailApiResponse,
      PreflightFinancingEndingEmailApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/financing-ending/email/preflight`,
        method: 'POST',
        body: queryArg.financingEndingPreflightRequestBody,
      }),
      // Recipient names and addresses are not kept in the cache once unused.
      keepUnusedDataFor: 0,
      extraOptions: {
        responseSchema: FinancingEndingPreflightResponseBodySchema,
      },
    }),
    // A mutation, so that a written body never becomes a cache key.
    previewEditableFinancingEndingEmail: build.mutation<
      PreviewEditableFinancingEndingEmailApiResponse,
      PreviewEditableFinancingEndingEmailApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/financing-ending/email/preview`,
        method: 'POST',
        body: queryArg.financingEndingPreviewRequestBody,
      }),
      extraOptions: {
        responseSchema: FinancingEndingPreviewResponseBodySchema,
      },
    }),
    prepareFinancingEndingEmailSource: build.mutation<
      PrepareFinancingEndingEmailSourceApiResponse,
      PrepareFinancingEndingEmailSourceApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/financing-ending/email/source`,
        method: 'POST',
        body: queryArg.financingEndingEmailSourceRequestBody,
      }),
      // A gateway error does not prove that no source was stored, so the base query must not
      // prepare a second one.
      extraOptions: {
        responseSchema: FinancingEndingEmailSourceResponseBodySchema,
        skipInfrastructureRetry: true,
      },
    }),
    submitFinancingEndingBulkAction: build.mutation<
      SubmitFinancingEndingBulkActionApiResponse,
      SubmitFinancingEndingBulkActionApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/bulk-operation/resource/${FINANCING_ENDING_BULK_RESOURCE_ID}/action/${queryArg.actionCode}`,
        method: 'POST',
        body: queryArg.submitBulkActionRequestBody,
      }),
      // A gateway error does not prove that no operation was started, so the base query must not
      // send the e-mails a second time.
      extraOptions: {
        responseSchema: FinancingEndingSubmitBulkActionResponseBodySchema,
        skipInfrastructureRetry: true,
      },
    }),
    getFinancingEndingBulkOperation: build.query<
      GetFinancingEndingBulkOperationApiResponse,
      GetFinancingEndingBulkOperationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/bulk-operation/${queryArg.bulkOperationId}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'FinancingEndingBulkOperation', id: queryArg.bulkOperationId},
      ],
      transformResponse: (response: FinancingEndingBulkOperationWire) =>
        mapFinancingEndingBulkOperation(response),
      extraOptions: {
        responseSchema: FinancingEndingBulkOperationWireSchema,
      },
    }),
    listFinancingEndingBulkOperationItems: build.query<
      ListFinancingEndingBulkOperationItemsApiResponse,
      ListFinancingEndingBulkOperationItemsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/bulk-operation/${queryArg.bulkOperationId}/item`,
        params: {
          limit: queryArg.limit,
          offset: queryArg.offset,
          state: queryArg.state,
        },
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'FinancingEndingBulkOperationItems', id: queryArg.bulkOperationId},
      ],
      keepUnusedDataFor: ITEMS_KEEP_UNUSED_DATA_FOR_SECONDS,
      transformResponse: (response: ListFinancingEndingBulkOperationItemsWire) =>
        response.map(mapFinancingEndingBulkOperationItem),
      extraOptions: {
        responseSchema: ListFinancingEndingBulkOperationItemsWireSchema,
      },
    }),
    rerunFailedFinancingEndingBulkOperation: build.mutation<
      RerunFailedFinancingEndingBulkOperationApiResponse,
      RerunFailedFinancingEndingBulkOperationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/bulk-operation/${queryArg.bulkOperationId}/rerun-failed`,
        method: 'POST',
      }),
      // A gateway error does not prove that the failed items were not started again, so the base
      // query must not send the e-mails a second time.
      extraOptions: {
        responseSchema: RerunFailedFinancingEndingBulkOperationApiResponseSchema,
        skipInfrastructureRetry: true,
      },
    }),
  }),
});

export const {
  usePreflightFinancingEndingEmailQuery,
  usePreviewEditableFinancingEndingEmailMutation,
  usePrepareFinancingEndingEmailSourceMutation,
  useSubmitFinancingEndingBulkActionMutation,
  useGetFinancingEndingBulkOperationQuery,
  useListFinancingEndingBulkOperationItemsQuery,
  useRerunFailedFinancingEndingBulkOperationMutation,
} = financingEndingApi;
